import { Configuration, OrdersApi } from '@/api';
import Button from '@/components/Button';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import PageHeader from '@/components/PageHeader';
import { useSettings } from '@/contexts/SettingsContext';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import CartMoqAlert from '@/modules/store/components/CartMoqAlert';
import OrderReviewTable from '@/modules/store/components/OrderReviewTable';
import { useCart } from '@/modules/store/contexts/CartContext';
import { findStoreAddress, StoreAddress, StoreAddressType } from '@/modules/store/models/Store';
import CartEmpty from '@/modules/store/pages/CartEmpty';
import { CartPath, CheckoutSuccessPath } from '@/modules/store/paths';
import useCartQuery from '@/modules/store/queries/useCartQuery';
import CartDetailsService from '@/modules/store/services/CartDetailsService';
import { formatTimeWindow } from '@/utils/date/formatTimeWindow';
import { formatCityRegionPostal } from '@/utils/formatAddress';
import { useNavigate } from 'react-router-dom';

const CheckoutReview = () => {
  const navigate = useNavigate();

  const { currentUser } = useAuth();
  const { storeId } = currentUser();
  const { currencyCode } = useSettings();

  const ordersApi = new OrdersApi(new Configuration(useAuth));

  const {
    getSelectedCartsAndItems,
    cartItemCount,
    removeSelectedCartsAndItems,
    canCheckout,
    addedSkuIds,
    currentSkuIds,
    isInitialized,
  } = useCart();

  const { isLoading, skus, suppliers, store } = useCartQuery(
    isInitialized ? addedSkuIds : undefined,
    isInitialized ? currentSkuIds : undefined,
    storeId,
  );

  if (!store) {
    return;
  }

  if (isLoading || !isInitialized) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (!isLoading && cartItemCount == 0) {
    return <CartEmpty />;
  }

  const carts = getSelectedCartsAndItems();

  const cartService = new CartDetailsService();

  const orders = cartService.getCartDetails(carts, skus, suppliers, currencyCode);
  const taxCountryCode = findStoreAddress(store?.addresses, [
    StoreAddressType.BILLING,
    StoreAddressType.BUSINESS,
    StoreAddressType.DELIVERY,
  ])?.countryCode;

  const handleCheckoutOnClick = async () => {
    try {
      const result = await ordersApi.bulkCreateOrders(
        orders.map((order) => ({
          ...order,
          storeId: store.id,
        })),
      );

      const orderCodes = result.map((item) => item.code);
      const supplierIds = orders.map((cart) => cart.supplierId);

      removeSelectedCartsAndItems();

      navigate(CheckoutSuccessPath.withIds(orderCodes, supplierIds.slice(0, carts.length)));
    } catch (e) {
      console.log('Error', e);
    }
  };

  const address = (address?: StoreAddress) => {
    if (!address) {
      return null;
    }

    const cityRegionPostal = formatCityRegionPostal(address);

    return (
      <>
        {address.building}
        <br />
        {address.street1}
        <br />
        {address.street2 !== '' && (
          <>
            {address.street2}
            <br />
          </>
        )}
        {cityRegionPostal && (
          <>
            {cityRegionPostal}
            <br />
          </>
        )}
        {address.countryCode}
      </>
    );
  };

  return (
    <>
      <PageHeader title="Checkout Review">
        <Button data-testid="checkout-review-back-button" to={CartPath} variant="secondary">
          Back to Checkout
        </Button>
        <Button
          data-testid="checkout-review-submit-order-button"
          onClick={handleCheckoutOnClick}
          disabled={!canCheckout(orders)}
        >
          Submit Order
        </Button>
      </PageHeader>

      <div className="flex flex-row w-full rounded-lg bg-white px-3 py-6 border-1 border-gray-500 shadow mb-8">
        <div className="flex-1 px-3">
          <span className="block font-bold mb-1">Billing Address:</span>

          <p className="block mb-1 ">
            {address(
              findStoreAddress(store?.addresses, [
                StoreAddressType.BILLING,
                StoreAddressType.BUSINESS,
              ]),
            )}
          </p>
        </div>
        <div className="flex-1 px-3">
          <span className="block font-bold mb-1">Delivery Address:</span>

          <p className="block mb-1">
            {address(
              findStoreAddress(store?.addresses, [
                StoreAddressType.DELIVERY,
                StoreAddressType.BUSINESS,
              ]),
            )}
          </p>
        </div>
      </div>

      <div>
        {orders.map((order) => (
          <div
            key={order.supplierId}
            className="mb-8"
            data-testid="checkout-review-supplier-section"
            data-supplier-name={order.supplierName}
            data-supplier-id={order.supplierId}
          >
            <h2
              className="text-xl font-bold mb-4"
              data-testid="checkout-review-supplier-heading"
              data-supplier-name={order.supplierName}
              data-supplier-id={order.supplierId}
            >
              {order.supplierName}
            </h2>

            <CartMoqAlert order={order} />

            <div className="mb-4 lg:w-96">
              <span className="block mb-1 font-semibold">Delivery date:</span>
              <div>{formatTimeWindow(order.deliveryWindow)}</div>
            </div>

            <OrderReviewTable order={order} showVat={store.showVat} countryCode={taxCountryCode} />
          </div>
        ))}
      </div>
    </>
  );
};

export default CheckoutReview;
